import './assets/styles/global.css';
import { Suspense } from 'react';
import { adminRouter, type MyRouterContext } from './router/routes.tsx';
import { UseWorkspaceConfigs } from '@r/hooks/use-workspace-config.ts';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { RouterProvider } from '@tanstack/react-router';
import { AuthLoader, useUser } from '@r/api/auth.tsx';
import { useRoutes } from '@r/hooks/use-routes.ts';
import { Loader } from '@r/components/loader/loader.tsx';
import i18n from '@r/lib/i18n.ts';
import { useTranslation } from 'react-i18next';
import { useWorkspaceFormStore } from '@admin/context/workspace-form-context.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { getMaintenancePageReason, shouldShowMaintenancePage } from '@r/lib/maintenance-error.ts';
import { MaintenanceErrorPage } from '@r/page/error/maintenance-error-page.tsx';

const App = () => {
  const { ready } = useTranslation(undefined, { i18n });
  const user = useUser();
  const workspaceConfig = UseWorkspaceConfigs(false);
  const routes = useRoutes();

  const hasSession = localStorage.getItem('token') !== null;

  // Boot : attendre config workspace, user, i18n et routes (si connecté)
  const isLoading =
    !i18n.isInitialized ||
    !ready ||
    workspaceConfig.isLoading ||
    user.isLoading ||
    (hasSession && routes.isLoading);

  if (isLoading) {
    return <Loader />;
  }

  // Boot : 503 (maintenance) ou 500 whitelist après retries → page maintenance.
  if (workspaceConfig.isError && shouldShowMaintenancePage(workspaceConfig.error)) {
    return <MaintenanceErrorPage reason={getMaintenancePageReason(workspaceConfig.error)} />;
  }

  if (user.isError && shouldShowMaintenancePage(user.error)) {
    return <MaintenanceErrorPage reason={getMaintenancePageReason(user.error)} />;
  }

  if (hasSession && routes.isError && shouldShowMaintenancePage(routes.error)) {
    return <MaintenanceErrorPage reason={getMaintenancePageReason(routes.error)} />;
  }

  // Autres erreurs de boot (réseau, 4xx…) : message minimal, pas de maintenance
  if (workspaceConfig.isError || user.isError || (hasSession && routes.isError)) {
    return (
      <div className="flex justify-center items-center min-h-screen text-sm text-zinc-600">
        Impossible de charger l&apos;application pour le moment.
      </div>
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <WorkspaceConfigContext.Provider value={workspaceConfig.data}>
        <AuthLoader renderLoading={() => <Loader />}>
          <AdminRouter
            user={user.data ?? null}
            routes={routes.data ?? []}
            routesLoaded={routes.isSuccess}
          />
        </AuthLoader>
      </WorkspaceConfigContext.Provider>
    </Suspense>
  );
};

const AdminRouter = (props: Omit<MyRouterContext, 'workSpaceCreateFormData'>) => {
  const workspaceFormState = useWorkspaceFormStore();
  const activeWorkspace = props.user
    ? getActiveWorkspaceUniversal(props.user.visibleWorkspaces)
    : null;
  const activeWorkspaceId = activeWorkspace?.workspace.id as number;
  const formData = workspaceFormState?.workspaces?.[activeWorkspaceId] ?? null;

  return (
    <RouterProvider
      router={adminRouter}
      context={{
        user: props.user,
        routes: props.routes,
        routesLoaded: props.routesLoaded,
        workSpaceCreateFormData: formData
      }}
    />
  );
};

export default App;
